import React, { useRef, useEffect, ReactNode, useState, useCallback } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import ReactGA from 'react-ga'
import { Location } from '@reach/router'
import { throttle } from 'throttle-debounce'
import { useIsomorphicLayoutEffect } from 'react-use'

import { ClipsPopUp } from '@/components/common/clips-pop-up'
import { CTAKey } from '@/components/common/cta'
import { mobile } from '@/utils/media-query'
import { DocsSidebar } from '@/components/common/docs-sidebar'
import { env } from '@/config'
import { gaCode } from '@/config/ga'
import { Header } from '@/components/common/header'
import { NavigationContext } from '@/components/common/context-link/context-link'
import { theme } from '@/styles'

const DOCS_VIEWPORT_CONTENT = 'width=1240, user-scalable=yes, shrink-to-fit=no'

const LayoutContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`

const HeaderContainer = styled.div<{ hasMargin?: boolean }>`
  margin: 0 auto;
  position: sticky;
  top: 0;
  background: black;
  z-index: 2;
  margin-bottom: ${({ hasMargin, theme }) =>
    hasMargin === false ? '0' : `${theme.headerMargin}px`};
  width: 100%;

  ${mobile.css`
        margin-bottom: 10px;
    `}
`

// center the docs, fixed width
// TODO: sidebar is fixed, which breaks horizontal scroll
const DocsContainerComponent = styled.div`
  display: flex;
  width: ${({ theme }) => theme.containerWidth}px;
  margin: 0 auto;
`

const DocsContainer: React.FC = ({ children }) => {
  // react-helmet does not clean up so hooks are used instead
  useIsomorphicLayoutEffect(() => {
    const viewportElement = document.querySelector('meta[name="viewport"]')
    if (viewportElement) {
      const previousContent = viewportElement.getAttribute('content')
      viewportElement.setAttribute('content', DOCS_VIEWPORT_CONTENT)
      return () => {
        if (previousContent) viewportElement.setAttribute('content', previousContent)
      }
    }
  }, [])
  return <DocsContainerComponent>{children}</DocsContainerComponent>
}

const ContentContainer = styled.div`
  width: ${({ theme }) => theme.contentWidth}px;
`

// don't let content be smaller than header,
// for when screen is smaller than header
// hide page overflow for marquee
const LandingPageContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`

// Vertical offset to prevent wrong items in sidebar to be highlighted as active
// because of scroll event firing for anchor navigation
const VERTICAL_OFFSET = 50

const ScrollSpyContainer = ({
  children,
  onNavigationChange,
}: {
  children: ReactNode
  onNavigationChange: (route: string) => void
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const getAnchors = useCallback(() => {
    if (!containerRef.current) {
      return []
    }

    const elements = containerRef.current.querySelectorAll<HTMLElement>('[data-type="anchor"]')
    return Array.from(elements).map((item) => ({
      position: item.offsetTop,
      hash: `#${item.id}`,
      pathname: item.getAttribute('data-pathname'),
    }))
  }, [])

  useEffect(
    () => {
      onNavigationChange(window.location.pathname + window.location.hash)
      const el = document.getElementById(window.location.hash.substr(1))
      if (el) el.scrollIntoView()

      if (!containerRef || !containerRef.current) {
        return
      }

      // TODO: Consider adding scroll debounce for higher performance / responsiveness
      const handleScroll = throttle(400, () => {
        const anchors = getAnchors()
        if (!anchors.length) {
          return
        }

        const yPosition = window.pageYOffset + VERTICAL_OFFSET
        // Reset hash when viewport is above first anchor
        if (yPosition < anchors[0].position) {
          window.history.pushState(null, '', window.location.pathname)
          onNavigationChange(window.location.pathname)
          return
        }

        let activeAnchor = null

        // Loop through page anchors and find lowest anchor above current viewport position
        for (let i = 1, prev = anchors[i - 1], cur = anchors[i]; (cur = anchors[i]); i++) {
          prev = anchors[i - 1]

          if (yPosition >= prev.position && yPosition < cur.position) {
            activeAnchor = prev
            break
          }

          activeAnchor = cur
        }

        if (activeAnchor && window.location.hash !== activeAnchor.hash) {
          const path = activeAnchor.pathname
            ? activeAnchor.pathname + activeAnchor.hash
            : window.location.pathname + activeAnchor.hash
          window.history.pushState(null, '', path)
          onNavigationChange(path)
        }
      })

      window.addEventListener('scroll', handleScroll)
      return () => window.removeEventListener('scroll', handleScroll)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return <ContentContainer ref={containerRef}>{children}</ContentContainer>
}

type BaseLayoutProps = {
  withSidebar?: boolean
  headerCTAKey?: CTAKey
}

export const BaseLayout: React.FC<BaseLayoutProps> = ({
  children,
  withSidebar = false,
  headerCTAKey = 'default',
}) => {
  const [route, setRoute] = useState('')

  useEffect(() => {
    ReactGA.initialize(gaCode, { debug: env.GA_DEBUG })
  }, [])

  return (
    <LayoutContainer>
      <NavigationContext.Provider value={{ onNavigationChange: setRoute }}>
        <Location>
          {({ location }) => (
            <ThemeProvider
              theme={{ ...theme, disableMobileQueries: location.pathname.includes('docs') }}
            >
              <ClipsPopUp enabled={true} />
              <Location>
                {(location) => (
                  <HeaderContainer hasMargin={!location.location.pathname.includes('faq')}>
                    <Header ctaKey={headerCTAKey} />
                  </HeaderContainer>
                )}
              </Location>
              {withSidebar ? (
                <DocsContainer>
                  <DocsSidebar route={route} />
                  <ScrollSpyContainer onNavigationChange={setRoute}>{children}</ScrollSpyContainer>
                </DocsContainer>
              ) : (
                <LandingPageContainer>{children}</LandingPageContainer>
              )}
            </ThemeProvider>
          )}
        </Location>
      </NavigationContext.Provider>
    </LayoutContainer>
  )
}

export default BaseLayout
